/**
 * Astrologer Admin - Payouts Management
 * Handle astrologer withdrawal requests and payout processing
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Avatar,
  Alert,
} from '@mui/material';
import {
  Search,
  Visibility,
  Refresh,
  AccountBalance,
  CheckCircle,
  Cancel,
  Payment,
  Schedule,
  TrendingUp,
  Download,
} from '@mui/icons-material';
import axios from 'axios';
import { api_urls } from '../../../utils/api-urls';
import { toast } from 'react-toastify';

const AstrologerPayouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [stats, setStats] = useState({
    pendingCount: 0,
    pendingAmount: 0,
    processedCount: 0,
    processedAmount: 0,
    todayRequests: 0
  });

  useEffect(() => {
    fetchPayouts();
    fetchStats();
  }, [statusFilter, dateFilter]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFilter !== 'all') params.dateRange = dateFilter;

      const response = await axios.get(`${api_urls}api/astrologer/admin/payouts`, { params });
      setPayouts(response.data?.payouts || []);
    } catch (error) {
      console.error('Error fetching payouts:', error);
      // Demo data
      setPayouts([
        {
          _id: '1',
          requestId: 'WD001',
          astrologer: {
            _id: 'ast1',
            astrologerName: 'Pandit Ramesh Sharma',
            phone: '9876543210',
            photo: 'https://via.placeholder.com/50'
          },
          amount: 5000,
          bankDetails: {
            accountName: 'Ramesh Sharma',
            accountNumber: '1234567890123',
            ifscCode: 'SBIN0001234',
            bankName: 'State Bank of India'
          },
          upiId: 'ramesh@upi',
          paymentMethod: 'bank',
          walletBalance: 8500,
          totalEarnings: 45000,
          status: 'pending',
          requestedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date().toISOString(),
        },
        {
          _id: '2',
          requestId: 'WD002',
          astrologer: {
            _id: 'ast2',
            astrologerName: 'Guru Lakshmi Devi',
            phone: '9876543211',
            photo: 'https://via.placeholder.com/50'
          },
          amount: 8000,
          bankDetails: {
            accountName: 'Lakshmi Devi',
            accountNumber: '9876543210987',
            ifscCode: 'HDFC0001234',
            bankName: 'HDFC Bank'
          },
          upiId: null,
          paymentMethod: 'bank',
          walletBalance: 12000,
          totalEarnings: 78000,
          status: 'processing',
          requestedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date().toISOString(),
        },
        {
          _id: '3',
          requestId: 'WD003',
          astrologer: {
            _id: 'ast3',
            astrologerName: 'Jyotishi Manoj Kumar',
            phone: '9876543212',
            photo: 'https://via.placeholder.com/50'
          },
          amount: 3500,
          bankDetails: null,
          upiId: 'manoj.jyotish@paytm',
          paymentMethod: 'upi',
          walletBalance: 6500,
          totalEarnings: 32000,
          status: 'completed',
          transactionId: 'TXN123456789',
          processedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          requestedAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date().toISOString(),
        },
        {
          _id: '4',
          requestId: 'WD004',
          astrologer: {
            _id: 'ast4',
            astrologerName: 'Acharya Suresh Pandey',
            phone: '9876543213',
            photo: 'https://via.placeholder.com/50'
          },
          amount: 2000,
          bankDetails: {
            accountName: 'Suresh Pandey',
            accountNumber: '5678901234567',
            ifscCode: 'ICIC0001234',
            bankName: 'ICICI Bank'
          },
          upiId: null,
          paymentMethod: 'bank',
          walletBalance: 500,
          totalEarnings: 18000,
          status: 'rejected',
          rejectionReason: 'Bank account details mismatch with KYC',
          requestedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${api_urls}api/astrologer/admin/payouts/stats`);
      setStats(response.data || {
        pendingCount: 12,
        pendingAmount: 67500,
        processedCount: 156,
        processedAmount: 425000,
        todayRequests: 8
      });
    } catch (error) {
      setStats({
        pendingCount: 12,
        pendingAmount: 67500,
        processedCount: 156,
        processedAmount: 425000,
        todayRequests: 8
      });
    }
  };

  const getStatusChip = (status) => {
    const config = {
      pending: { color: 'warning', label: 'Pending' },
      processing: { color: 'info', label: 'Processing' },
      completed: { color: 'success', label: 'Completed' },
      rejected: { color: 'error', label: 'Rejected' },
    };
    const cfg = config[status] || config.pending;
    return <Chip size="small" color={cfg.color} label={cfg.label} />;
  };

  const handleProcess = async () => {
    if (!transactionId.trim()) {
      toast.error('Please enter transaction ID');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${api_urls}api/astrologer/admin/payouts/${selectedPayout._id}/process`, {
        transactionId: transactionId.trim()
      });
      toast.success('Payout processed successfully');
      setProcessDialogOpen(false);
      setTransactionId('');
      fetchPayouts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process payout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please select/enter rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${api_urls}api/astrologer/admin/payouts/${selectedPayout._id}/reject`, {
        reason: rejectReason.trim()
      });
      toast.success('Payout request rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      fetchPayouts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject payout');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredPayouts = payouts.filter(p =>
    p.requestId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.astrologer?.astrologerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.astrologer?.phone?.includes(searchTerm) ||
    p.transactionId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#9C27B0' }}>
        <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
        Astrologer Payouts
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule sx={{ color: '#f57c00' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                  {stats.pendingCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">Pending Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#fce4ec' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#c2185b' }}>
                ₹{stats.pendingAmount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">Pending Amount</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: '#388e3c' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c' }}>
                  {stats.processedCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">Processed Payouts</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                ₹{stats.processedAmount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">Total Processed</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                {stats.todayRequests}
              </Typography>
              <Typography variant="body2" color="textSecondary">Today's Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Request ID, Astrologer, Phone, Transaction ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { fetchPayouts(); fetchStats(); }}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              fullWidth
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Payouts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Request ID</TableCell>
              <TableCell>Astrologer</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Wallet Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredPayouts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No payout requests found
                </TableCell>
              </TableRow>
            ) : (
              filteredPayouts.map((payout) => (
                <TableRow key={payout._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {payout.requestId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={payout.astrologer?.photo} sx={{ width: 32, height: 32 }} />
                      <Box>
                        <Typography variant="body2">{payout.astrologer?.astrologerName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {payout.astrologer?.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      ₹{payout.amount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={payout.paymentMethod === 'upi' ? 'UPI' : 'Bank Transfer'} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">₹{payout.walletBalance.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(payout.status)}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(payout.requestedAt).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPayout(payout);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {payout.status === 'pending' && (
                        <>
                          <Tooltip title="Process Payout">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setSelectedPayout(payout);
                                setProcessDialogOpen(true);
                              }}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedPayout(payout);
                                setRejectDialogOpen(true);
                              }}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#9C27B0', color: 'white' }}>
          Payout Details - {selectedPayout?.requestId}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedPayout && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity={selectedPayout.status === 'pending' ? 'warning' : 
                  selectedPayout.status === 'completed' ? 'success' : 
                  selectedPayout.status === 'rejected' ? 'error' : 'info'}>
                  Status: {selectedPayout.status.toUpperCase()}
                  {selectedPayout.rejectionReason && ` - ${selectedPayout.rejectionReason}`}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Astrologer</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Avatar src={selectedPayout.astrologer?.photo} />
                  <Box>
                    <Typography>{selectedPayout.astrologer?.astrologerName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedPayout.astrologer?.phone}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Withdrawal Amount</Typography>
                <Typography fontWeight={700} color="success.main" variant="h6">
                  ₹{selectedPayout.amount.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Current Wallet Balance</Typography>
                <Typography fontWeight={600}>₹{selectedPayout.walletBalance.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Payment Method</Typography>
                <Chip 
                  label={selectedPayout.paymentMethod === 'upi' ? 'UPI' : 'Bank Transfer'} 
                  size="small" 
                />
              </Grid>
              {selectedPayout.paymentMethod === 'bank' && selectedPayout.bankDetails && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Bank Details</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Account Name</Typography>
                    <Typography>{selectedPayout.bankDetails.accountName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Account Number</Typography>
                    <Typography>{selectedPayout.bankDetails.accountNumber}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">IFSC Code</Typography>
                    <Typography>{selectedPayout.bankDetails.ifscCode}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Bank Name</Typography>
                    <Typography>{selectedPayout.bankDetails.bankName}</Typography>
                  </Grid>
                </>
              )}
              {selectedPayout.paymentMethod === 'upi' && selectedPayout.upiId && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">UPI ID</Typography>
                  <Typography fontWeight={600}>{selectedPayout.upiId}</Typography>
                </Grid>
              )}
              {selectedPayout.transactionId && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Transaction ID</Typography>
                  <Typography fontWeight={600} color="primary">{selectedPayout.transactionId}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Process Dialog */}
      <Dialog open={processDialogOpen} onClose={() => setProcessDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4CAF50', color: 'white' }}>
          Process Payout
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Processing payout of <strong>₹{selectedPayout?.amount?.toLocaleString()}</strong> to{' '}
            <strong>{selectedPayout?.astrologer?.astrologerName}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Transaction ID / UTR Number"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            margin="normal"
            required
            placeholder="Enter bank transaction ID or UTR"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setProcessDialogOpen(false); setTransactionId(''); }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleProcess}
            disabled={actionLoading}
            startIcon={actionLoading && <CircularProgress size={16} />}
          >
            Mark as Processed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          Reject Payout Request
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to reject payout request of{' '}
            <strong>₹{selectedPayout?.amount?.toLocaleString()}</strong>?
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Rejection Reason</InputLabel>
            <Select
              value={rejectReason}
              label="Rejection Reason"
              onChange={(e) => setRejectReason(e.target.value)}
            >
              <MenuItem value="Bank account details mismatch with KYC">Bank account details mismatch with KYC</MenuItem>
              <MenuItem value="Invalid bank account number">Invalid bank account number</MenuItem>
              <MenuItem value="Invalid IFSC code">Invalid IFSC code</MenuItem>
              <MenuItem value="UPI ID verification failed">UPI ID verification failed</MenuItem>
              <MenuItem value="Insufficient wallet balance">Insufficient wallet balance</MenuItem>
              <MenuItem value="KYC not approved">KYC not approved</MenuItem>
              <MenuItem value="Suspicious activity detected">Suspicious activity detected</MenuItem>
              <MenuItem value="Other">Other (specify below)</MenuItem>
            </Select>
          </FormControl>
          {rejectReason === 'Other' && (
            <TextField
              fullWidth
              label="Specify Reason"
              value=""
              onChange={(e) => setRejectReason(e.target.value)}
              margin="normal"
              multiline
              rows={2}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialogOpen(false); setRejectReason(''); }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleReject}
            disabled={actionLoading}
            startIcon={actionLoading && <CircularProgress size={16} />}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AstrologerPayouts;
